import React from 'react';

const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Space', 'Spacebar']);

/**
 * Returns true when the supplied keyboard event represents an activation intent.
 */
export function isActivationKey(event: React.KeyboardEvent<HTMLElement>): boolean {
  return ACTIVATION_KEYS.has(event.key);
}

/**
 * Normalises keyboard activation so custom interactive elements respond to Enter and Space.
 * Prevents re-entrancy when the event originated from a descendant element.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function handleKeyboardActivation<T extends HTMLElement>(
  event: React.KeyboardEvent<T>,
  handler: (element: T) => void,
) {
  if (!isActivationKey(event)) {
    return;
  }

  if (event.target !== event.currentTarget) {
    return;
  }

  event.preventDefault();
  handler(event.currentTarget);
}

export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );

  return candidates.filter((element) => {
    if (element.hasAttribute('disabled')) {
      return false;
    }
    if (element.getAttribute('aria-hidden') === 'true') {
      return false;
    }
    if (element.tabIndex < 0) {
      return false;
    }
    const isVisible =
      element.offsetWidth > 0 ||
      element.offsetHeight > 0 ||
      element.getClientRects().length > 0 ||
      element === document.activeElement;
    return isVisible;
  });
}
